import { useCallback, useEffect, useMemo } from 'react';

import isEqual from 'lodash-es/isEqual';
import set from 'lodash-es/set';
import { useDispatch, useSelector } from 'react-redux';

import useBrains from '@/hooks/useBrains';
import useLocalStorage from '@/hooks/useLocalStorage';
import {
  LockedByBrain,
  pruneStaleBrains,
  removeVariable as removeLockedVariable,
  syncVariableValues,
  upsertVariable,
  writeBrainEntry,
} from '@/modules/tryIt/components/VariablesSection/useLockedContextVariables';
import { selectAccountId } from '@/redux/session/selectors';
import { Context } from '@/shared/types/chat';

import { ContextVariable, flattenRawContext, useContextVariables } from './useContextVariables';
import { updateContext } from '../redux/actions';
import { selectBrainId, selectContext } from '../redux/selectors';

// Shared with the legacy modal so a variable locked in either stays locked.
const LOCKED_VARIABLES_STORAGE_KEY = 'tryItLockedContextVariables';
type LockedValue = LockedByBrain[string]['variables'][number]['value'];

const EMPTY_LOCKED_LIST: { label: string; value: LockedValue }[] = [];

export type LockableVariable = ContextVariable & { isLocked: boolean };

/**
 * New-modal port of the legacy locked-context-variables behavior: variables can
 * be pinned so they survive "Reset conversation" and page reloads. Reuses the
 * legacy pure helpers and localStorage key; reads/writes the `tryIt` slice.
 */
export const useLockedContextVariables = () => {
  const dispatch = useDispatch();
  const accountId = useSelector(selectAccountId);
  const brainId = useSelector(selectBrainId);
  const storeContext = useSelector(selectContext);
  const { context, variables, replaceContext } = useContextVariables();

  const { brains } = useBrains();
  const [lockedByBrain, setLockedByBrain] = useLocalStorage<LockedByBrain>(
    LOCKED_VARIABLES_STORAGE_KEY,
    {}
  );

  // Self-heal: drop entries for deleted brains on the current account.
  useEffect(() => {
    if (!brains || !accountId) {
      return;
    }
    const validBrainIds = new Set(brains.map((b) => b.brain_id));
    setLockedByBrain((prev) => pruneStaleBrains(prev, accountId, validBrainIds) ?? prev);
  }, [accountId, brains, setLockedByBrain]);

  const lockedForBrain = useMemo(
    () => (brainId && lockedByBrain[brainId]?.variables) || EMPTY_LOCKED_LIST,
    [brainId, lockedByBrain]
  );

  const rawByLabel = useMemo(
    () =>
      new Map(
        flattenRawContext(context).map((variable) => [
          variable.label,
          variable.value as LockedValue,
        ])
      ),
    [context]
  );

  const lockableVariables = useMemo<LockableVariable[]>(
    () =>
      variables.map((variable) => ({
        ...variable,
        isLocked: lockedForBrain.some((v) => v.label === variable.label),
      })),
    [variables, lockedForBrain]
  );

  // Re-hydrate locked variables into the context once it collapses to empty —
  // i.e. on first open and after "Reset conversation" (`resetTest` clears it to
  // `[]`). Preserves locked values across resets and reloads.
  useEffect(() => {
    if (storeContext.length !== 0 || lockedForBrain.length === 0) {
      return;
    }
    const newContext: Context = {};
    lockedForBrain.forEach(({ label, value }) => set(newContext, label, value));
    dispatch(updateContext([newContext]));
  }, [dispatch, lockedForBrain, storeContext]);

  // Keep the persisted snapshot in sync when a brain run mutates a locked
  // variable's value mid-conversation, so a later reset re-hydrates the latest.
  useEffect(() => {
    if (!brainId || !accountId || lockedForBrain.length === 0) {
      return;
    }
    const nextVariables = syncVariableValues(lockedForBrain, rawByLabel);
    if (nextVariables === lockedForBrain) {
      return;
    }
    setLockedByBrain((prev) => writeBrainEntry(prev, brainId, accountId, nextVariables));
  }, [accountId, brainId, rawByLabel, lockedForBrain, setLockedByBrain]);

  const setAllLocked = useCallback(
    (locked: boolean) => {
      if (!brainId || !accountId || lockableVariables.length === 0) {
        return;
      }
      const nextVariables = locked
        ? lockableVariables.map(({ label, value }) => ({
            label,
            value: rawByLabel.get(label) ?? value,
          }))
        : [];
      setLockedByBrain((prev) => writeBrainEntry(prev, brainId, accountId, nextVariables));
    },
    [accountId, brainId, lockableVariables, rawByLabel, setLockedByBrain]
  );

  const replaceUnlocked = useCallback(
    (incoming: Context): { skippedLocked: boolean } => {
      const lockedLabels = lockableVariables.filter((v) => v.isLocked).map((v) => v.label);
      const submittedByLabel = new Map(
        flattenRawContext(incoming).map((entry) => [entry.label, entry.value])
      );
      const skippedLocked = lockedLabels.some(
        (label) =>
          !submittedByLabel.has(label) ||
          !isEqual(submittedByLabel.get(label), rawByLabel.get(label))
      );
      replaceContext(incoming, lockedLabels);
      return { skippedLocked };
    },
    [lockableVariables, rawByLabel, replaceContext]
  );

  const toggleLock = useCallback(
    (variable: LockableVariable) => {
      if (!brainId || !accountId) {
        return;
      }
      setLockedByBrain((prev) => {
        const existing = prev[brainId]?.variables ?? [];
        const nextVariables = variable.isLocked
          ? removeLockedVariable(existing, variable.label)
          : upsertVariable(
              existing,
              variable.label,
              rawByLabel.get(variable.label) ?? variable.value
            );
        return writeBrainEntry(prev, brainId, accountId, nextVariables);
      });
    },
    [accountId, brainId, rawByLabel, setLockedByBrain]
  );

  return { lockableVariables, toggleLock, setAllLocked, replaceUnlocked };
};
